// @avantia common-code
import { ensureDefined, intervalToMs, voidFunction } from '@avantia/client-and-server-utilities';
import { lodash } from '@avantia/lodash';
import { fetchResolverBasic } from '../actions/fetchResolver';
import { HpViewportChangedAction } from '../actions/viewportChangedActionTypes';
import { HpClientDebugFlags } from '../clientDebugFlags';
import { logNotice } from '../clientLogging';
import { registerConfigurationAction } from '../config/registerClientConfig';
import { HpAction, HpEngineActionTypes, HpFetchError, HpFetchErrorPayload, HpUserInterfaceState } from '../types';
import {
  HpAjaxCallCompletedAction,
  HpAjaxCallStartedAction,
  HpErrorPageDisplayedAction,
  HpFetchErrorOcurredAction
} from '../types/ajaxCallActionTypes';
import { getInitialState } from './initialState';
import { applyChanges } from './reducerLibrary';
import { ReducerProcessor } from './reducerProcessor';

const minMsToShowError = intervalToMs('10s');

const processor = new ReducerProcessor<HpUserInterfaceState, HpEngineActionTypes>('UserInterface');

let showErrorPageAfterConsecutiveErrorCount: number;
registerConfigurationAction((config) => {
  ({ showErrorPageAfterConsecutiveErrorCount } = config);
});

processor.add<HpFetchErrorOcurredAction>('FETCH_ERROR_OCCURRED', fetchErrorReducer);

processor.add<HpAjaxCallStartedAction>('AJAX_CALL_STARTED', (state, { payload }) => {
  const { ajaxCallsMap } = state;
  let updatedAjaxCallsMap = {};
  const ajaxCallsInProgress = state.ajaxCallsInProgress + 1;
  if (payload) {
    const { ajaxCallId, loadingPageType } = payload as unknown as HpAjaxCallStartedAction;
    updatedAjaxCallsMap = { ...ajaxCallsMap, [ajaxCallId]: loadingPageType };
  }

  return {
    ...state,
    ajaxCallsInProgress,
    disableButtons: ajaxCallsInProgress > 0,
    ajaxCallsMap: payload ? updatedAjaxCallsMap : ajaxCallsMap
  };
});

processor.add<HpAjaxCallCompletedAction>('AJAX_CALL_COMPLETED', (state, { payload }) => {
  const { ajaxCallId } = payload as unknown as HpAjaxCallCompletedAction;
  let { fetchError } = state;
  const { ajaxCallsInProgress, ajaxCallsMap } = updateAjaxCallsDetails(state, ajaxCallId);

  if (fetchError && fetchError.timestamp + minMsToShowError < new Date().getTime()) {
    fetchError = undefined;
  }

  return {
    ...state,
    ajaxCallsInProgress,
    disableButtons: ajaxCallsInProgress > 0,
    fetchError,
    ajaxCallsMap
  };
});

processor.add<HpErrorPageDisplayedAction>('ERROR_PAGE_DISPLAYED', (state) => {
  const { fetchError } = state;
  if (!fetchError) {
    return { ...state };
  }

  return {
    ...state,
    fetchError: { ...fetchError, showErrorPage: false }
  };
});

processor.add<HpViewportChangedAction>('VIEWPORT_CHANGED', (state, { payload }) => {
  const { viewport } = state;
  const { height, width } = viewport;
  const { height: newHeight, width: newWidth } = ensureDefined(payload, 'payload');
  if (height !== newHeight || width !== newWidth) {
    return applyChanges(state, { viewport: payload });
  }

  return state;
});

export function fetchErrorReducer(
  state: HpUserInterfaceState,
  action: HpFetchErrorOcurredAction
): HpUserInterfaceState {
  const {
    fetchError: ferror,
    description,
    ajaxCallId,
    errorDetails,
    showErrorPage = true,
    shouldIncrementErrorCount = true,
    logErrorToServer
  } = action;

  const { ajaxCallsInProgress, ajaxCallsMap } = updateAjaxCallsDetails(state, ajaxCallId);
  const { fetchError: prevFetchError } = state;
  const errorCode = getErrorCode(action.errorCode, ferror, errorDetails);

  const message =
    action.message ||
    (lodash.isObject(ferror) ? ferror.message : action.fetchError) ||
    'An unexpected error has occurred.';

  let fetchError: HpFetchError;

  if (typeof ferror === 'string') {
    fetchError = {
      error: ferror,
      message,
      description,
      timestamp: new Date().getTime(),
      errorDetails,
      showErrorPage,
      errorCode
    };
  } else {
    const { error, message, timestamp } = (ferror || {}) as HpFetchErrorPayload;
    fetchError = {
      error,
      message,
      description,
      timestamp: (timestamp || new Date()).getTime(),
      showErrorPage,
      errorCode
    };
  }

  if (logErrorToServer !== false) {
    // Log the error to the server
    fetchResolverBasic<string, void>({
      url: '/api/scriptError',
      description: 'script error',
      method: 'POST',
      data: JSON.stringify(fetchError)
    })
      .then(voidFunction)
      .catch(voidFunction);
  }

  const errorCount = getErrorCount(prevFetchError, fetchError, shouldIncrementErrorCount);
  const maxErrorCountReached = errorCount >= showErrorPageAfterConsecutiveErrorCount;
  if (maxErrorCountReached) {
    logNotice(`Maximum error count reached for error: ${errorCode || description}.`, HpClientDebugFlags.None);
  }

  return {
    ...state,
    fetchError: {
      ...fetchError,
      count: errorCount,
      showErrorPage: showErrorPage || maxErrorCountReached
    },
    ajaxCallsInProgress: ajaxCallsInProgress,
    disableButtons: false,
    ajaxCallsMap
  };
}

function getErrorCode(
  errorCode: string | undefined,
  ferror: string | HpFetchErrorPayload | undefined,
  errorDetails: any
): string | undefined {
  if (errorCode === undefined) {
    if (ferror) {
      const { errorCode: code } = ferror as HpFetchErrorPayload;
      if (code) {
        errorCode = code;
      }
    }

    if (!errorCode && errorDetails) {
      const { errorCode: code } = errorDetails as HpFetchErrorPayload;
      if (code) {
        errorCode = code;
      }
    }
  }

  return errorCode;
}

function getErrorCount(
  prevFetchError: HpFetchError | undefined,
  fetchError: HpFetchError,
  shouldIncrementErrorCount = true
): number {
  const { errorCode: prevCode, description: prevDescription, count: prevCount } = prevFetchError || {};
  const { errorCode, description } = fetchError || {};
  let count = prevCount || 1;
  if (
    shouldIncrementErrorCount &&
    prevFetchError &&
    ((prevCode && prevCode === errorCode) || prevDescription === description)
  ) {
    count++;
  }

  return count;
}

export function userInterfaceReducer(
  state: HpUserInterfaceState = getInitialState().userInterface,
  action: HpAction<unknown>
): HpUserInterfaceState {
  return processor.process(state, action);
}

function updateAjaxCallsDetails(state: HpUserInterfaceState, ajaxCallId?: string): HpUserInterfaceState {
  const { ajaxCallsInProgress, ajaxCallsMap } = state;
  let callsInProgress = ajaxCallsInProgress - 1;
  let updatedAjaxCallsMap = {};
  if (ajaxCallsInProgress < 0) {
    callsInProgress = 0;
  }

  if (ajaxCallId) {
    updatedAjaxCallsMap = lodash.omit(ajaxCallsMap, ajaxCallId);
  }

  return {
    ...state,
    ajaxCallsInProgress: callsInProgress,
    ajaxCallsMap: ajaxCallId ? updatedAjaxCallsMap : ajaxCallsMap
  };
}
